import type React from "react";
import type { CSSProperties, ReactNode } from "react";

/**
 * Stavební prvky UI dle /Design/Prototyp.dc.html (artboard p7b).
 *
 * Všechno kreslí ze sémantických tokenů (`styles/tokens.css`), takže jeden
 * kód obslouží tmavý i světlý motiv. **Nikdy sem nepiš barvu natvrdo** —
 * ani `rgba(…)`, ani hex; jinak se světlý motiv rozpadne.
 *
 * Veřejné API zůstává schválně stejné jako před redesignem: `Tone`,
 * `toneColor` a `withAlpha` používá přes padesát komponent a migrují se
 * postupně. Změnil se jen jejich vnitřek.
 */

export type Tone = "cyan" | "green" | "amber" | "red" | "dim" | "violet";

/**
 * Tón → sémantická role. Názvy tónů jsou dědictví (barvy), role je to,
 * co se skutečně vykreslí. Nové kódy pojmenovávej podle významu.
 */
const toneRole: Record<Tone, string> = {
  cyan: "accent",
  green: "positive",
  amber: "warning",
  red: "critical",
  violet: "info",
  dim: "text-tertiary",
};

export const toneColor: Record<Tone, string> = {
  cyan: "var(--accent)",
  green: "var(--positive)",
  amber: "var(--warning)",
  red: "var(--critical)",
  violet: "var(--info)",
  dim: "var(--text-tertiary)",
};

/**
 * Průhledná varianta tónu. Dřív vracela natvrdo `rgba(47,230,255,…)`, což
 * byla hlavní překážka světlého motivu — barva se nedala přepnout. Teď se
 * míchá až v prohlížeči z aktuální hodnoty tokenu.
 */
export function withAlpha(tone: Tone, alpha: number): string {
  const pct = Math.round(Math.max(0, Math.min(1, alpha)) * 100);
  return `color-mix(in srgb, ${toneColor[tone]} ${pct}%, transparent)`;
}

/** Připravené plochy stavu — levnější a konzistentnější než `withAlpha`. */
export const toneBg = (tone: Tone) =>
  tone === "dim" ? "var(--surface-inset)" : `var(--${toneRole[tone]}-bg)`;
export const toneBorder = (tone: Tone) =>
  tone === "dim" ? "var(--border-default)" : `var(--${toneRole[tone]}-border)`;
export const toneSubtle = (tone: Tone) =>
  tone === "dim" ? "var(--surface-inset)" : `var(--${toneRole[tone]}-subtle)`;

/**
 * Barva textu, který stojí na ploše vlastního tónu (chip, sekundární
 * tlačítko). Plný tón by si tam kontrast ujedl sám — viz `--*-on-tint`.
 */
export const toneOnTint = (tone: Tone) =>
  tone === "dim" ? "var(--text-secondary)" : `var(--${toneRole[tone]}-on-tint)`;

/**
 * Důraz. V tmavém motivu zář, ve světlém sytost a barevný stín — proto se
 * nikdy nepíše `box-shadow` s barvou napřímo (PROTOTYP §5.5).
 */
export const emphasis = (tone: Tone) =>
  tone === "dim" ? "var(--shadow-panel)" : `var(--emphasis-${toneRole[tone]})`;

/**
 * Panel/karta. Bez `backdrop-filter` schválně: rozostření z prvku dělá
 * containing block pro `position: fixed`, takže modál otevřený uvnitř panelu
 * by se kotvil k panelu místo k oknu (PROTOTYP p7c/3 — tenhle problém se
 * jednou už řešil portálovým hackem). Rozostření proto nesou jen plovoucí
 * plochy: rail, modály a popovery.
 */
export function Panel({
  children,
  tone,
  brackets = false,
  className = "",
  style,
  ...rest
}: {
  children: ReactNode;
  tone?: Tone;
  /**
   * Zvýrazněný panel. Dřív to kreslilo rohové závorky; ty se k zaobleným
   * rohům nového designu nehodí, takže se význam („tenhle panel je
   * důležitý") vyjadřuje akcentním okrajem a jemným důrazem.
   */
  brackets?: boolean;
  className?: string;
  style?: CSSProperties;
} & Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "className" | "style"
>) {
  const accent = tone ?? (brackets ? "cyan" : undefined);
  return (
    <div
      className={`relative ${className}`}
      style={{
        borderRadius: "var(--radius-card)",
        border: `1px solid ${accent ? toneBorder(accent) : "var(--border-default)"}`,
        background: accent ? toneSubtle(accent) : "var(--surface)",
        boxShadow:
          brackets && accent ? emphasis(accent) : "var(--shadow-panel)",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * Hlavička obrazovky podle p1a: velký nadpis, za ním monospace dovětek
 * („Rozhodnutí // INBOX") a pod ním jedna věta, která říká, k čemu stránka je.
 *
 * Dřív si každá obrazovka kreslila vlastní `h1` s vlastní velikostí; proto
 * vypadala každá jinak a žádná jako návrh.
 */
export function PageHeader({
  title,
  kicker,
  lead,
  right,
}: {
  title: ReactNode;
  /** Mono dovětek za nadpisem — bez „//", to doplní komponenta. */
  kicker?: string;
  lead?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start gap-x-5 gap-y-3">
      <div className="min-w-0">
        <h1 className="af-page-title">
          {title}
          {kicker ? <span className="af-page-kicker"> // {kicker}</span> : null}
        </h1>
        {lead ? <div className="af-page-lead">{lead}</div> : null}
      </div>
      {right ? (
        <div className="ml-auto flex flex-wrap items-center gap-2.5">
          {right}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Hlavička detailní obrazovky (p6f, p6j): zpětný odkaz, nadpis a štítky na
 * jednom řádku, akce vpravo. Detaily si tenhle řádek dřív skládaly samy a
 * rozjely se — tři velikosti nadpisu a dvě barvy zpětného odkazu napříč
 * šesti obrazovkami.
 */
export function DetailHeader({
  back,
  title,
  meta,
  right,
}: {
  /** Odkaz „← někam". Barvu dědí od hlavičky, ať je všude stejná. */
  back?: ReactNode;
  title: ReactNode;
  /** Štítky vedle nadpisu (stav, severita, oddělení). */
  meta?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3.5 gap-y-2">
      {back ? (
        <span
          className="shrink-0 text-[12px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          {back}
        </span>
      ) : null}
      <h1 className="af-detail-title min-w-0 max-w-full truncate">{title}</h1>
      {meta}
      {right ? (
        <div className="ml-auto flex flex-wrap items-center gap-2">{right}</div>
      ) : null}
    </div>
  );
}

export function SectionHeader({
  label,
  sub,
  tone = "cyan",
  right,
  className = "",
}: {
  label: string;
  sub?: string;
  tone?: Tone;
  right?: ReactNode;
  className?: string;
}) {
  return (
    // `min-w-0` a zalomení: popisek s prostrkáním 0,16 em je široký a bez
    // toho vytlačil doplněk z panelu ven (DEFINITION OF DONE + „konfigurovatelné
    // per codebase" přetékalo přes okraj karty)
    <div
      className={`flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 ${className}`}
    >
      <div
        className="af-label min-w-0 break-words"
        style={{ color: toneColor[tone] }}
      >
        {label}
        {sub ? (
          <span
            className="break-words"
            style={{ color: "var(--text-tertiary)" }}
          >
            {" "}
            {sub}
          </span>
        ) : null}
      </div>
      {right ? (
        <div className="af-label shrink-0" style={{ color: "var(--accent)" }}>
          {right}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Filtr v hlavičce obrazovky (p1a, p6g, p6h). Pilulka, ne hranaté tlačítko;
 * aktivní nese plochu tónu a vnitřní prstenec, ostatní jen tenký okraj.
 *
 * Není monospace — v návrhu jsou popisky filtrů čtený text, na rozdíl od
 * štítků stavu (`Chip`).
 */
export function FilterPill({
  children,
  active,
  onClick,
  tone = "cyan",
  title,
  testId,
}: {
  children: ReactNode;
  active: boolean;
  onClick: () => void;
  tone?: Tone;
  title?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      data-testid={testId}
      className="cursor-pointer px-4 py-[7px] text-[12px] font-semibold transition-colors"
      style={{
        borderRadius: "var(--radius-pill)",
        background: active ? toneBg(tone) : "var(--surface-inset)",
        color: active ? toneOnTint(tone) : "var(--text-secondary)",
        border: active
          ? "1px solid transparent"
          : "1px solid var(--border-default)",
        boxShadow: active ? `inset 0 0 0 1px ${toneBorder(tone)}` : undefined,
      }}
    >
      {children}
    </button>
  );
}

export function GlowDot({
  tone = "cyan",
  pulse = true,
  size = 7,
}: {
  tone?: Tone;
  pulse?: boolean;
  size?: number;
}) {
  return (
    <span
      aria-hidden
      className={pulse ? "af-animate-pulse" : undefined}
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: 999,
        background: toneColor[tone],
        boxShadow: emphasis(tone),
        flexShrink: 0,
      }}
    />
  );
}

export function Chip({
  children,
  tone = "cyan",
  filled = false,
  className = "",
}: {
  children: ReactNode;
  tone?: Tone;
  filled?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`af-mono inline-flex items-center gap-1 px-2.5 py-[3px] text-[10px] font-bold uppercase ${className}`}
      style={{
        borderRadius: "var(--radius-pill)",
        color: filled ? "var(--text-on-accent)" : toneOnTint(tone),
        background: filled ? toneColor[tone] : toneBg(tone),
      }}
    >
      {children}
    </span>
  );
}

export function StatTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
}) {
  return (
    <Panel className="px-4 py-3">
      <div className="af-label-dim">{label}</div>
      <div
        className="af-display mt-1 text-[22px] font-semibold"
        style={{ color: tone ? toneColor[tone] : "var(--text-primary)" }}
      >
        {value}
      </div>
      {sub ? (
        <div className="af-label-dim mt-0.5 normal-case">{sub}</div>
      ) : null}
    </Panel>
  );
}

export function ProgressBar({
  value,
  tone = "cyan",
  height = 3,
  className = "",
}: {
  value: number; // 0–100
  tone?: Tone;
  height?: number;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        height,
        background: "var(--surface-inset)",
        borderRadius: 999,
        position: "relative",
        overflow: "hidden",
      }}
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          width: `${Math.max(0, Math.min(100, value))}%`,
          background: toneColor[tone],
          borderRadius: 999,
          transition: "width .4s ease",
        }}
      />
    </div>
  );
}

export function ProgressRing({
  value,
  size = 52,
  tone = "cyan",
  label,
}: {
  value: number; // 0–100
  size?: number;
  tone?: Tone;
  label?: ReactNode;
}) {
  const stroke = 3;
  const r = (size - stroke * 2) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <svg
        width={size}
        height={size}
        style={{ transform: "rotate(-90deg)" }}
        aria-hidden
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--surface-inset)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={toneColor[tone]}
          strokeWidth={stroke}
          strokeDasharray={`${(clamped / 100) * c} ${c}`}
          strokeLinecap="round"
        />
      </svg>
      <div
        className="af-display"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size / 3.4,
          fontWeight: 600,
          color: "var(--text-primary)",
        }}
      >
        {label ?? clamped}
      </div>
    </div>
  );
}

type BtnVariant = "primary" | "secondary" | "ghost" | "danger";

const sizeClass: Record<"sm" | "md" | "lg", string> = {
  sm: "px-3 py-[5px] text-[11px]",
  md: "px-[18px] py-2 text-[13px]",
  lg: "h-11 px-[26px] text-[14px]",
};

const radiusFor: Record<"sm" | "md" | "lg", string> = {
  sm: "var(--radius-chip)",
  md: "var(--radius-control)",
  lg: "var(--radius-row)",
};

export function Btn({
  children,
  onClick,
  variant = "ghost",
  tone = "cyan",
  size = "md",
  type = "button",
  disabled,
  className = "",
  title,
  ...rest
}: {
  children: ReactNode;
  /** Událost se předává dál — tlačítka uvnitř klikatelných karet musí umět zastavit bublání. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: BtnVariant;
  tone?: Tone;
  /**
   * `sm` = drobná akce v hlavičce panelu (p5a: 11 px, poloměr 8),
   * `lg` = hlavní akce obrazovky (p1a: výška 44, 14 px, poloměr 12).
   */
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit";
  disabled?: boolean;
  className?: string;
  title?: string;
} & Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "onClick" | "type" | "disabled" | "className" | "title"
>) {
  const t: Tone = variant === "danger" ? "red" : tone;

  const look: Record<BtnVariant, CSSProperties> = {
    // primární akce nese akcentní gradient — je v aplikaci vždy jen jedna
    primary: {
      background: "var(--accent-gradient)",
      color: "var(--text-on-accent)",
      border: "1px solid transparent",
      boxShadow: emphasis("cyan"),
      fontWeight: 700,
    },
    // sekundární nese plochu tónu a vnitřní prstenec místo okraje —
    // vedle primárního tlačítka pak nedělá druhý rám
    secondary: {
      background: toneBg(t),
      color: toneOnTint(t),
      border: "1px solid transparent",
      boxShadow: `inset 0 0 0 1px ${toneBorder(t)}`,
      fontWeight: 600,
    },
    ghost: {
      background: "transparent",
      color: "var(--text-secondary)",
      border: "1px solid var(--border-strong)",
      fontWeight: 600,
    },
    danger: {
      background: "transparent",
      color: "var(--critical)",
      border: `1px solid ${toneBorder("red")}`,
      fontWeight: 600,
    },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      {...rest}
      className={`inline-flex cursor-pointer items-center justify-center gap-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        sizeClass[size]
      } ${className}`}
      style={{ borderRadius: radiusFor[size], ...look[variant] }}
    >
      {children}
    </button>
  );
}

export function EmptyState({ title, note }: { title: string; note?: string }) {
  return (
    <div className="px-4 py-6 text-center">
      <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>
        {title}
      </div>
      {note ? (
        <div
          className="mt-1"
          style={{ color: "var(--text-tertiary)", fontSize: 11 }}
        >
          {note}
        </div>
      ) : null}
    </div>
  );
}

export function Field({
  label,
  children,
  hint,
  error,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  /** Chyba se ukazuje místo nápovědy a vždy s ikonou — stav nikdy jen barvou. */
  error?: string;
}) {
  return (
    <label className="block">
      <div
        className="af-mono mb-[3px] text-[9px] tracking-[0.14em] uppercase"
        style={{ color: "var(--text-tertiary)" }}
      >
        {label}
        {hint ? (
          <span style={{ color: "var(--text-disabled)" }}> · {hint}</span>
        ) : null}
      </div>
      {children}
      {error ? (
        <div
          className="mt-[3px] text-[10px]"
          style={{ color: "var(--critical)" }}
        >
          ✕ {error}
        </div>
      ) : null}
    </label>
  );
}

/**
 * Sdílená třída vstupů. `--surface-inset` nahradil konvenci „select potřebuje
 * vlastní pozadí" — nativní select se v tmavém motivu kreslil světle, takže
 * si každý call site nastavoval `background: var(--surface-inset)` ručně.
 */
export const inputClass =
  "af-mono w-full rounded-[var(--radius-control)] border border-[var(--border-field)] bg-[var(--surface-inset)] px-3 py-2.5 text-[12px] text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-disabled)] focus:border-[var(--border-focus)] focus:shadow-[var(--focus-ring)]";
